import { useMemo, useRef, useEffect } from 'react'
import { useReportExportView } from './reportExportViewContext'
import ReportDataGrid from './ReportDataGrid'
import ReportGroupedGrid from './ReportGroupedGrid'
import { ReportRunStateContext, useReportHasRun } from './reportRunStateContext'
import useGridControls from './useGridControls'
import { makeAutoColumns, fmtMoney, fmtNumber, looksLikeMoneyKey } from './autoReportColumns'
import { useAliasedLabel, usePublishAliasColumns } from './columnAliasContext'
import { escapeHtml } from './escapeHtml'

/**
 * CC-49304 — routes the specialized reports that render via <AutoReportTable>
 * (plain array of row objects, auto-detected columns) through the universal
 * ReportDataGrid, so they pick up sort / filter / show-hide / resize / persisted
 * layout. Drop-in replacement for <AutoReportTable> in the in-scope reports
 * (Fee Allocation, Payments-by-Case, Bill Journal, Matter Metrics, Status).
 *
 * Reuses AutoReportTable's exact column derivation + formatting (makeAutoColumns)
 * so labels/alignment/number formatting are identical to before — this only
 * changes the surrounding grid, not the values.
 *
 * These reports have no server total row; AutoReportTable summed its sum-columns
 * client-side in a footer. We preserve that as the totals strip. Because each
 * total equals the sum of every row, CC-49382's guardrail correctly classifies
 * them as recomputable SUM columns when filtered.
 *
 * CC — shared toolbar: the same client-side Group By + column-value filter that
 * CsvReportGrid offers (useGridControls) now renders above this grid too, so
 * the six AutoReportTable-derived reports get identical controls. Column ids
 * are positional indexes (like CsvReportGrid) so groupFlatReport's SUM-only
 * group subtotals resolve against the footer strip.
 */
// CC-50013 (G3): saved-view page state is forwarded to the underlying grid —
// see the matching note in CsvReportGrid.
/*
 * `suppressStripActions`: a page that mounts MORE THAN ONE of these (StatusReport's
 * multi-section digest) would otherwise get one host-published strip button per
 * section, because the button travels by context and every grid underneath reads
 * it. One "Rename columns" per digest section is both wrong and ambiguous — they
 * would all edit the same per-report alias map. Off by default, so the four pages
 * that mount exactly one AutoReportGrid keep the button.
 */
const AutoReportGrid = ({ rows = [], columns: overrides, storageKey, rowNoun, pageStateRef, initialPageState, suppressStripActions = false }) => {
  // CC-49594: publish the grid's export view to the surrounding shell (if
  // any) so it can offer a client-built PDF — same wiring as CsvReportGrid.
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => exportViewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx])
  // CC-50291: per-firm column names, resolved by the stable row key.
  const aliasedLabel = useAliasedLabel()
  const report = useMemo(() => {
    const cols = overrides?.length ? overrides : makeAutoColumns(rows)
    // CC-50291: the firm's own word for this column, if an admin typed one.
    // Keyed by `c.key` — the ROW KEY, which is stable — and deliberately NOT by
    // `columnId`, which is the positional index below. A positional alias key
    // would re-point every alias after a hidden or reordered column onto the
    // wrong column, and on a financial report that is a mislabelled figure
    // rather than a cosmetic slip. `columnId` stays positional because it is
    // this reshape's internal pairing with `dataCells`, not a stored identity.
    const columns = cols.map((c, i) => ({
      columnId: i,
      label: aliasedLabel(c.key, c.label),
      rawLabel: c.label,
      visible: true,
      columnAlignment: c.align === 'right' ? 'RIGHT' : 'LEFT',
    }))
    const reportData = rows.map((r) => ({
      isGroup: false,
      dataCells: cols.map((c, i) => {
        // Numeric columns (summed, or right-aligned) get a dataValue so sort + SUM
        // stay value-based; text columns sort on their display string.
        const numeric = c.numeric ?? (c.sum || c.align === 'right')
        return {
          columnId: i,
          // Spec 193: the grids render displayValue as HTML, and these rows are
          // CB's raw JSON — firm text, not pre-escaped grid cells. So a column's
          // `render` returns TEXT and is escaped like a raw value. `renderHtml` is
          // the explicit opt-in for markup cb2 writes itself, and must escape any
          // firm text it interpolates.
          displayValue: c.renderHtml
            ? String(c.renderHtml(r) ?? '')
            : escapeHtml(c.render ? c.render(r) : r[c.key]),
          dataValue: numeric ? Number(r[c.key] ?? 0) : undefined,
        }
      }),
    }))
    const hasSum = cols.some((c) => c.sum)
    const reportFooterCells = hasSum
      ? cols
          .map((c, idx) => {
            if (idx === 0) return { columnId: idx, displayValue: `Total (${rows.length})` }
            if (!c.sum) return { columnId: idx, displayValue: '' }
            const total = rows.reduce((acc, r) => acc + Number(r[c.key] ?? 0), 0)
            return {
              columnId: idx,
              displayValue: looksLikeMoneyKey(c.key) ? fmtMoney(total) : fmtNumber(total, 1),
            }
          })
          .filter((c) => c.displayValue !== '')
      : []
    return { columns, reportData, reportFooterCells, aliasColumns: cols.map((c) => ({ key: c.key, label: c.label })) }
  }, [rows, overrides, aliasedLabel])

  // CC-50291: publish this report's real columns so the shell can offer
  // "Rename columns" — a specialized report's columns exist only here.
  // CB's own labels, never the aliased ones; the editor shows both.
  const publishAliasColumns = usePublishAliasColumns()
  useEffect(() => {
    publishAliasColumns?.(report.aliasColumns)
  }, [publishAliasColumns, report.aliasColumns])

  // CC — shared Group By + value-filter toolbar (identical to CsvReportGrid's).
  const { controls, displayReport, grouped, groupBy } = useGridControls(report, storageKey)
  const hasRun = useReportHasRun()

  // Shadow the host's value rather than reaching for a prop: the grids read
  // this from context, so the only way to hide the button from them is to
  // provide a different value above them. hasRun is preserved deliberately —
  // suppressing the button must not also change the run control's state.
  const gridBody = (
    <>
      {grouped ? (
        <ReportGroupedGrid key={`grp:${groupBy}`} report={displayReport} storageKey={storageKey} exportViewRef={exportViewRef} pageStateRef={pageStateRef} initialPageState={initialPageState} headerRow={controls} />
      ) : (
        <ReportDataGrid report={displayReport} storageKey={storageKey} rowNoun={rowNoun} exportViewRef={exportViewRef} pageStateRef={pageStateRef} initialPageState={initialPageState} headerRow={controls} />
      )}
    </>
  )

  return (
    /* `controls` rides into the grid card as headerRow — see CsvReportGrid. */
    <div className="space-y-2">
      {suppressStripActions
        ? <ReportRunStateContext.Provider value={{ hasRun, stripActions: null }}>{gridBody}</ReportRunStateContext.Provider>
        : gridBody}
    </div>
  )
}

export default AutoReportGrid
